import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Button, Typography, message } from 'antd';
import { CaretLeftOutlined } from '@ant-design/icons';
import { updatePassword } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import ContentLayout from '../../components/ContentLayout';
import { getJwtSub, userStore } from '../../features/auth';
import { UserService } from '../../services/users';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [userId, setUserId] = useState('');

  const user = useMemo(() => userStore.getUser() as { email: string; fullname: string; isExternal?: boolean } | null, []);

  useEffect(() => {
    setUserId(getJwtSub());
    profileForm.setFieldsValue({
      name: user?.fullname || '',
      email: user?.email || '',
    });
    setLoadingProfile(false);
  }, [profileForm, user?.email, user?.fullname]);

  const handleUpdateProfile = async (values: { name: string; email: string }) => {
    try {
      setSavingProfile(true);
      const name = values.name?.trim();
      if (!userId) {
        message.error('User ID not found');
        return;
      }

      await UserService.updateUserProfile(userId, { fullName: name });

      userStore.setUser({
        email: values.email,
        fullname: name,
      });

      message.success('Profile updated successfully');
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Failed to update profile';
      message.error(errMsg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    try {
      setChangingPassword(true);
      await updatePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Password updated successfully');
      passwordForm.resetFields();
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Failed to update password';
      message.error(errMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <Header
        title="Profile"
        extraActions={
          <Button type="default" size="small" className="gap-1" icon={<CaretLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />
      <ContentLayout>
        <div className="max-w-5xl mx-auto">
          <Card loading={loadingProfile} className="rounded-xl shadow-sm border border-gray-200">
            <div className="mb-5 border-b border-gray-200 pb-3">
              <Title level={4} style={{ marginBottom: 4 }}>
                My Profile
              </Title>
              <Text type="secondary">Manage account details and password from one place.</Text>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col">
                <Form form={profileForm} layout="vertical" className="mt-3 flex flex-col h-full" onFinish={handleUpdateProfile}>
                  <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
                    <Input placeholder="Name" />
                  </Form.Item>
                  <Form.Item name="email" label="Email">
                    <Input disabled />
                  </Form.Item>
                  <div className="flex justify-end mt-auto">
                    <Button type="primary" htmlType="submit" loading={savingProfile}>
                      Update Profile
                    </Button>
                  </div>
                </Form>
              </div>

              {user?.isExternal ? (
                <div className="lg:border-l lg:border-gray-200 lg:pl-8 flex flex-col items-center justify-center text-center text-gray-500 py-10">
                  <div className="mb-2 text-4xl">🏢</div>
                  <Text type="secondary" className="max-w-xs">
                    Your password is managed securely by your organization's directory (Azure AD). 
                    To change it, please use your company's portal.
                  </Text>
                </div>
              ) : (
                <div className="lg:border-l lg:border-gray-200 lg:pl-8">
                  <Form form={passwordForm} layout="vertical" className="mt-3" onFinish={handleChangePassword}>
                    <Form.Item
                      name="currentPassword"
                      label="Current Password"
                      rules={[{ required: true, message: 'Please enter current password' }]}>
                      <Input.Password placeholder="Current Password" />
                    </Form.Item>
                    <Form.Item
                      name="newPassword"
                      label="New Password"
                      rules={[
                        { required: true, message: 'Please enter new password' },
                        { min: 8, message: 'Password must be at least 8 characters' },
                      ]}>
                      <Input.Password placeholder="New Password" />
                    </Form.Item>
                    <Form.Item
                      name="confirmPassword"
                      label="Confirm New Password"
                      dependencies={['newPassword']}
                      rules={[
                        { required: true, message: 'Please confirm new password' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match'));
                          },
                        }),
                      ]}>
                      <Input.Password placeholder="Confirm New Password" />
                    </Form.Item>
                    <div className="flex justify-end">
                      <Button type="primary" htmlType="submit" loading={changingPassword}>
                        Change Password
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
            </div>
          </Card>
        </div>
      </ContentLayout>
    </>
  );
};

export default Profile;
